import { createContext, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";

import { getOTPService, verifyOTPService } from "../services/authService";
import { parseAPIError } from "../utils/common";
import { showErrorToast, showSuccessToast } from "../utils/toast";

import { ContextProviderProps, VerifyOtpTypes } from "./types";

const defaultValue = {
  prospectId: "" as string,
  loading: false as Boolean,
  isAuthenticated: false as Boolean,
  verifyOTP: ({ prospectId, otp }: VerifyOtpTypes) => {},
  sendOTP: ({ prospectId }: { prospectId: string }) => {},
  setType: (type: string) => {},
  setIsAuthenticated: (type: boolean) => {},
};

export const AuthContext = createContext(defaultValue);

export const AuthProvider = ({ children }: ContextProviderProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [isAuthenticated, setIsAuthenticated] = useState<Boolean>(
    Boolean(sessionStorage.getItem("isAuthenticated")) ?? false
  );
  const [prospectId, setProspectId] = useState<string>(
    sessionStorage.getItem("prospectId") ?? ""
  );
  const [type, setType] = useState("");
  const [loading, setLoading] = useState<Boolean>(false);

  useEffect(() => {
    if (searchParams.get("prospectId")) {
      setProspectId(searchParams.get("prospectId") as string);
    }
  }, [searchParams]);

  const handleOnSuccessVerifyOTP = () => {
    setIsAuthenticated(true);
    sessionStorage.setItem("isAuthenticated", "true");
    sessionStorage.setItem("prospectId", prospectId);
    navigate(`/digital-contract/contract/details`);
  };

  const verifyOTPmutation = useMutation({
    onMutate: () => setLoading(true),
    mutationFn: verifyOTPService,
    onError: (error: any) => showErrorToast(parseAPIError(error)),
    onSuccess: handleOnSuccessVerifyOTP,
    onSettled: () => setLoading(false),
    retry: false,
  });

  const sendOTPMutation = useMutation({
    onMutate: () => setLoading(true),
    mutationFn: getOTPService,
    onError: (error: any) => showErrorToast(parseAPIError(error)),
    retry: false,
    onSettled: () => setLoading(false),
    ...(type === "resend" && {
      onSuccess: () =>
        showSuccessToast(
          "OTP has been successfully sent to your primary phone number."
        ),
    }),
  });

  const handleVerifyOTP = ({
    prospectId,
    otp,
    linkClickTime,
    otpEnteredTime,
  }: VerifyOtpTypes) =>
    verifyOTPmutation.mutate({
      prospectId,
      otp,
      linkClickTime,
      otpEnteredTime,
    } as VerifyOtpTypes);

  const handleSendOTP = ({ prospectId }: { prospectId: string }) => {
    sendOTPMutation.mutate(prospectId as string);
  };

  const authContextValue = {
    loading,
    prospectId,
    isAuthenticated,
    verifyOTP: handleVerifyOTP,
    sendOTP: handleSendOTP,
    setType,
    setIsAuthenticated,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};
